import type {
  ButtonHTMLAttributes,
  ChangeEvent,
  CSSProperties,
  FocusEvent,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';

export function Field({
  label,
  htmlFor,
  hint,
  required,
  /**
   * Plan v0.3.0 §G-3 follow-up — when `locked` is true, render a
   * "(잠금)" text marker next to the label with a hover tooltip
   * explaining that this field maps to a code-level enum (not
   * editable in EditableSelect). Use for OwnerEntity / RobotStatus /
   * VerificationStatus / DealStage / ContractStatus /
   * locationCategory etc. The visual cue helps operators
   * distinguish enum-locked picklists from OptionGroup-backed
   * `<EditableSelect>` ones at a glance.
   */
  locked,
  children,
}: {
  label: string;
  htmlFor?: string;
  hint?: string;
  required?: boolean;
  locked?: boolean;
  children: ReactNode;
}) {
  // 운영자 피드백 — 이전에는 outer wrapper 가 <label> 이라서 label 안의
  // 모든 클릭(빈 공간, hint 텍스트 포함)이 HTML 라벨 시맨틱에 의해
  // 연결된 input 으로 focus 를 전달했음. 결과: 카드 빈 영역을 클릭해도
  // 첫 input 이 focus / select 됨.
  //
  // 수정: outer wrapper 를 <div> 로 변경하고, 텍스트 라벨만 <label> 로
  // 감싸 htmlFor 로 명시 연결. label 텍스트를 직접 클릭해야 input 으로
  // focus 전달.
  return (
    <div style={{ display: 'block', marginBottom: 14 }}>
      <label
        htmlFor={htmlFor}
        className="kuun-label"
        style={{
          display: 'block',
          marginBottom: 4,
          color: 'var(--fg-default)',
          // label 텍스트만 클릭 가능 — 빈 영역은 cursor 가 default 로
          // 남도록 (inline-flex 가 아닌 block 으로 자연 wrap).
          cursor: htmlFor ? 'pointer' : 'default',
          width: 'fit-content',
        }}
      >
        {label}
        {required && <span style={{ color: 'var(--fg-danger)', marginLeft: 2 }}>*</span>}
        {locked && (
          <span
            title="이 항목의 선택지는 고정되어 있어 여기서 바꿀 수 없습니다."
            aria-label="선택지 고정"
            style={{
              marginLeft: 6,
              fontSize: 10,
              color: 'var(--fg-muted)',
              cursor: 'help',
            }}
          >
            (잠금)
          </span>
        )}
      </label>
      {children}
      {hint && (
        <div className="kuun-caption" style={{ display: 'block', marginTop: 4 }}>
          {hint}
        </div>
      )}
    </div>
  );
}

const baseInput: CSSProperties = {
  width: '100%',
  // FB3 §8 — content-box 기본값에서는 width:100% + padding + border 가 그리드
  // 셀보다 ~22px 넓어져 입력칸이 셀 경계를 넘쳐 클릭/포커스 범위가 어긋났다.
  // border-box 로 전역 정합(Input/Select/Textarea 모두 baseInput 소비).
  boxSizing: 'border-box',
  height: 34,
  padding: '0 10px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  fontSize: 13,
};

// round10 B6 — 한글 IME 조합 중 창 blur(다른 탭/페이지 전환)로 조합이 종료되면
// React controlled input 이 조합 중 억제한 onChange 를 흘려, 커밋된 마지막 음절이
// state 에 안 남고 사라진다("김사장"→"김사"). compositionend·blur 시점엔 DOM value
// 가 이미 최종값이므로, 그 값으로 onChange 를 강제 재발행해 state 를 맞춘다(멱등).
function flushOnChange<T extends HTMLInputElement | HTMLTextAreaElement>(
  onChange: ((e: ChangeEvent<T>) => void) | undefined,
  e: { currentTarget: T },
): void {
  onChange?.(e as unknown as ChangeEvent<T>);
}

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      onCompositionEnd={(e) => {
        flushOnChange(props.onChange, e);
        props.onCompositionEnd?.(e);
      }}
      onBlur={(e: FocusEvent<HTMLInputElement>) => {
        flushOnChange(props.onChange, e);
        props.onBlur?.(e);
      }}
      style={{ ...baseInput, ...props.style }}
    />
  );
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} style={{ ...baseInput, ...props.style }} />;
}

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      // round10 B6 — 위 Input 과 동일(IME 조합 종료/blur 시 마지막 음절 플러시).
      onCompositionEnd={(e) => {
        flushOnChange(props.onChange, e);
        props.onCompositionEnd?.(e);
      }}
      onBlur={(e: FocusEvent<HTMLTextAreaElement>) => {
        flushOnChange(props.onChange, e);
        props.onBlur?.(e);
      }}
      style={{
        ...baseInput,
        height: 'auto',
        minHeight: 80,
        padding: 8,
        resize: 'vertical',
        ...props.style,
      }}
    />
  );
}

export function Button({
  variant = 'primary',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  children?: ReactNode;
}) {
  const styles: Record<string, CSSProperties> = {
    primary: {
      background: 'var(--brand)',
      color: 'var(--brand-on)',
      border: 'none',
    },
    secondary: {
      background: 'var(--bg-surface)',
      color: 'var(--fg-default)',
      border: '1px solid var(--border-default)',
    },
    ghost: {
      background: 'transparent',
      color: 'var(--fg-muted)',
      border: 'none',
    },
    danger: {
      background: 'var(--fg-danger)',
      // round5 #21 — 채도 배경 위 텍스트 토큰화(다크 공통 흰색).
      color: 'var(--brand-on)',
      border: 'none',
    },
  };
  return (
    <button
      {...props}
      style={{
        height: 32,
        padding: '0 14px',
        borderRadius: 'var(--radius-sm)',
        fontSize: 13,
        fontWeight: 500,
        cursor: props.disabled ? 'not-allowed' : 'pointer',
        opacity: props.disabled ? 0.5 : 1,
        // round3 #5 — 아이콘+텍스트 버튼('+ 로봇 모델 추가')이 두 줄로 갈라지던 문제.
        // Icon SVG 가 display:block 이라 비-flex 버튼 안에서 자기 줄을 차지했음.
        // inline-flex + 중앙정렬 + nowrap 으로 한 줄 고정(텍스트 전용 버튼엔 무영향).
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        whiteSpace: 'nowrap',
        ...styles[variant],
        ...props.style,
      }}
    />
  );
}

export function FormError({ children }: { children?: ReactNode }) {
  if (!children) return null;
  return (
    <div
      role="alert"
      style={{
        margin: '12px 0',
        padding: '8px 10px',
        borderRadius: 'var(--radius-sm)',
        fontSize: 13,
        background: 'var(--bg-danger-subtle)',
        border: '1px solid var(--fg-danger)',
        color: 'var(--fg-danger)',
        whiteSpace: 'pre-wrap',
      }}
    >
      {children}
    </div>
  );
}
